import { useEffect, useState, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabaseClient';
import { Star, MapPin, DollarSign, Award, CheckCircle } from 'lucide-react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';

interface Trainer {
  id: string;
  name: string;
  bio: string | null;
  profile_image_url: string | null;
  hourly_rate: number;
  rating_average: number;
  total_reviews: number;
  experience_level: string;
  service_areas: string[] | null;
}

interface Review {
  id: string;
  rating: number;
  comment: string | null;
  reviewer_name: string;
  created_at: string;
}

interface Specialty {
  id: string;
  specialty_name: string;
  experience_level: string;
}

interface Certification {
  id: string;
  name: string;
  issuing_organization: string | null;
  issue_date: string | null;
}

export default function TrainerProfilePage() {
  const { id } = useParams<{ id: string }>();
  const { profile } = useAuth();
  const [trainer, setTrainer] = useState<Trainer | null>(null);
  const [reviews, setReviews] = useState<Review[]>([]);
  const [specialties, setSpecialties] = useState<Specialty[]>([]);
  const [certifications, setCertifications] = useState<Certification[]>([]);
  const [loading, setLoading] = useState(true);

  const loadTrainerData = useCallback(async () => {
    if (!id) return;

    try {
      // Load trainer
      const { data: trainerData, error: trainerError } = await supabase
        .from('trainers')
        .select('*')
        .eq('id', id)
        .single();

      if (trainerError) throw trainerError;
      setTrainer(trainerData as Trainer);

      // Load reviews
      const { data: reviewsData, error: reviewsError } = await supabase
        .from('reviews')
        .select('*, reviewer_id')
        .eq('trainer_id', id)
        .order('created_at', { ascending: false })
        .limit(10);

      if (!reviewsError && reviewsData) {
        const enrichedReviews = await Promise.all(
          reviewsData.map(async (review) => {
            const { data: reviewerProfile } = await supabase
              .from('profiles')
              .select('full_name')
              .eq('id', review.reviewer_id)
              .single();

            return {
              id: review.id,
              rating: review.rating,
              comment: review.comment,
              reviewer_name: reviewerProfile?.full_name || 'Anonymous',
              created_at: review.created_at,
            };
          })
        );
        setReviews(enrichedReviews);
      }

      // Load specialties
      const { data: specialtiesData } = await supabase
        .from('trainer_specialties')
        .select('*')
        .eq('trainer_id', id);

      if (specialtiesData) {
        setSpecialties(specialtiesData as Specialty[]);
      }

      // Load certifications
      const { data: certsData } = await supabase
        .from('trainer_certifications')
        .select('*')
        .eq('trainer_id', id);

      if (certsData) {
        setCertifications(certsData as Certification[]);
      }
    } catch (error) {
      console.error('Error loading trainer data:', error);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (id) {
      loadTrainerData();
    }
  }, [id, loadTrainerData]);

  if (loading) {
    return (
      <>
        <Header />
        <div className="flex min-h-screen items-center justify-center">
          <div className="text-center">
            <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-blue-600 border-r-transparent"></div>
            <p className="mt-4 text-sm text-gray-600">Loading...</p>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  if (!trainer) {
    return (
      <>
        <Header />
        <main className="min-h-screen bg-gray-50 py-8">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="rounded-xl border border-red-200 bg-red-50 p-8 text-center">
              <h2 className="text-xl font-semibold text-red-900">Trainer Not Found</h2>
              <p className="mt-2 text-sm text-red-700">
                The trainer you're looking for doesn't exist or has been removed.
              </p>
              <Link
                to="/trainer-discovery"
                className="mt-4 inline-block rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700"
              >
                Browse Trainers
              </Link>
            </div>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Header />
      <main className="min-h-screen bg-gray-50 py-8">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          {/* Trainer Header */}
          <div className="mb-8 rounded-xl border border-gray-200 bg-white p-8 shadow-sm">
            <div className="flex flex-col gap-6 md:flex-row">
              <div className="flex-shrink-0">
                <div className="h-32 w-32 overflow-hidden rounded-full bg-gray-200">
                  {trainer.profile_image_url ? (
                    <img
                      src={trainer.profile_image_url}
                      alt={trainer.name}
                      className="h-full w-full object-cover"
                    />
                  ) : (
                    <div className="flex h-full w-full items-center justify-center bg-blue-100 text-blue-600">
                      <span className="text-4xl font-semibold">
                        {trainer.name.charAt(0).toUpperCase()}
                      </span>
                    </div>
                  )}
                </div>
              </div>
              <div className="flex-1">
                <div className="mb-4 flex items-start justify-between">
                  <div>
                    <h1 className="text-3xl font-bold text-gray-900">{trainer.name}</h1>
                    <div className="mt-2 flex items-center gap-2">
                      <Star className="h-5 w-5 fill-yellow-400 text-yellow-400" />
                      <span className="text-lg font-semibold text-gray-700">
                        {trainer.rating_average?.toFixed(1) || '0.0'}
                      </span>
                      <span className="text-gray-500">
                        ({trainer.total_reviews || 0} reviews)
                      </span>
                      <CheckCircle className="ml-2 h-5 w-5 text-green-600" />
                      <span className="text-sm text-gray-600">Verified</span>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-2xl font-bold text-gray-900">
                      <DollarSign className="inline h-6 w-6" />
                      {trainer.hourly_rate?.toFixed(2) || '0.00'}/hr
                    </p>
                  </div>
                </div>
                {trainer.bio && (
                  <p className="mb-4 text-gray-600">{trainer.bio}</p>
                )}
                <div className="flex flex-wrap gap-4 text-sm text-gray-600">
                  <div className="flex items-center gap-2">
                    <Award className="h-4 w-4" />
                    <span className="capitalize">{trainer.experience_level} Level</span>
                  </div>
                  {trainer.service_areas && trainer.service_areas.length > 0 && (
                    <div className="flex items-center gap-2">
                      <MapPin className="h-4 w-4" />
                      <span>{trainer.service_areas.join(', ')}</span>
                    </div>
                  )}
                </div>
                {profile?.role === 'client' && (
                  <div className="mt-6">
                    <Link
                      to={`/trainer/${id}/book`}
                      className="inline-block rounded-lg bg-blue-600 px-6 py-3 text-sm font-semibold text-white hover:bg-blue-700"
                    >
                      Book Session
                    </Link>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Specialties */}
          {specialties.length > 0 && (
            <section className="mb-8">
              <h2 className="mb-4 text-2xl font-bold text-gray-900">Specialties</h2>
              <div className="flex flex-wrap gap-2">
                {specialties.map((specialty) => (
                  <span
                    key={specialty.id}
                    className="rounded-full bg-blue-100 px-4 py-2 text-sm font-semibold text-blue-700"
                  >
                    {specialty.specialty_name} ({specialty.experience_level})
                  </span>
                ))}
              </div>
            </section>
          )}

          {/* Certifications */}
          {certifications.length > 0 && (
            <section className="mb-8">
              <h2 className="mb-4 text-2xl font-bold text-gray-900">Certifications</h2>
              <div className="space-y-2">
                {certifications.map((cert) => (
                  <div
                    key={cert.id}
                    className="rounded-lg border border-gray-200 bg-white p-4"
                  >
                    <p className="font-semibold text-gray-900">{cert.name}</p>
                    {cert.issuing_organization && (
                      <p className="text-sm text-gray-600">{cert.issuing_organization}</p>
                    )}
                    {cert.issue_date && (
                      <p className="text-xs text-gray-500">
                        Issued: {new Date(cert.issue_date).toLocaleDateString()}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* Reviews */}
          <section>
            <h2 className="mb-4 text-2xl font-bold text-gray-900">Reviews</h2>
            {reviews.length === 0 ? (
              <div className="rounded-xl border border-gray-200 bg-white p-8 text-center">
                <p className="text-gray-600">No reviews yet</p>
              </div>
            ) : (
              <div className="space-y-4">
                {reviews.map((review) => (
                  <div
                    key={review.id}
                    className="rounded-xl border border-gray-200 bg-white p-6"
                  >
                    <div className="mb-2 flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <p className="font-semibold text-gray-900">{review.reviewer_name}</p>
                        <div className="flex">
                          {[...Array(5)].map((_, i) => (
                            <Star
                              key={i}
                              className={`h-4 w-4 ${
                                i < review.rating
                                  ? 'fill-yellow-400 text-yellow-400'
                                  : 'text-gray-300'
                              }`}
                            />
                          ))}
                        </div>
                      </div>
                      <p className="text-sm text-gray-500">
                        {new Date(review.created_at).toLocaleDateString()}
                      </p>
                    </div>
                    {review.comment && (
                      <p className="text-gray-600">{review.comment}</p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </section>
        </div>
      </main>
      <Footer />
    </>
  );
}
